// MemberLayout.jsx — Layout espace membre (white-label par asso, JAMAIS de mention AssoPerf)
// Topbar horizontale desktop + bottom nav mobile
import { useEffect, useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { applyTheme } from '@/lib/theme';
import { usePermissions } from '@/lib/usePermissions';
import {
  Home, Calendar, Ticket, User, LogOut, Loader2, Building2, MessageCircle,
  Newspaper, BadgeCheck, CalendarDays, Bell, BellOff, Activity, CreditCard, Receipt,
} from 'lucide-react';
import useUnreadNotifications from '@/lib/useUnreadNotifications';

export default function MemberLayout() {
  const { user, logout, setUser } = useAuth();
  const [asso, setAsso] = useState(null);
  const [loading, setLoading] = useState(true);
  const { can } = usePermissions();
  const { unread, perm, askPermission } = useUnreadNotifications({ enabled: can('messages', 'view') });

  useEffect(() => {
    (async () => {
      if (!user?.asso_id) { setLoading(false); return; }
      try {
        const { data } = await api.get('/assos/me');
        setAsso(data);
        if (data.couleur_principale) {
          applyTheme(data.couleur_principale);
          const merged = { ...user, couleur_principale: data.couleur_principale };
          localStorage.setItem('assoperf_user', JSON.stringify(merged));
          setUser(merged);
        }
      } catch {} finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F8] grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#94A3B8]" />
      </div>
    );
  }
  if (!asso) {
    return (
      <div className="min-h-screen bg-[#F9F9F8] grid place-items-center p-8">
        <div className="text-center max-w-md">
          <p className="font-display text-2xl font-bold text-[#0F172A] mb-3">
            Pas encore <span className="italic font-medium text-[#0047FF]">d'association.</span>
          </p>
          <p className="text-sm text-[#475569] mb-6">Vous n'êtes membre d'aucune association pour l'instant.</p>
          <button onClick={logout} className="btn-secondary">Se déconnecter</button>
        </div>
      </div>
    );
  }

  const initials = (asso.nom || '?').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

  const navItems = [
    { to: '/espace', icon: Home, label: 'Accueil', end: true, show: true, key: 'home' },
    { to: '/espace/evenements', icon: Calendar, label: 'Agenda', show: true, key: 'agenda' },
    { to: '/espace/annuaire', icon: Building2, label: 'Annuaire', show: can('annuaire', 'view'), key: 'annuaire' },
    { to: '/espace/messages', icon: MessageCircle, label: 'Messages', show: can('messages', 'view'), key: 'messages', badge: unread },
    { to: '/espace/gazette', icon: Newspaper, label: 'Gazette', show: can('gazette', 'view'), key: 'gazette' },
    { to: '/espace/agenda-pro', icon: CalendarDays, label: 'Agenda Pro', show: can('annuaire', 'view'), key: 'agenda-pro' },
    { to: '/espace/billets', icon: Ticket, label: 'Billets', show: true, key: 'billets' },
    { to: '/espace/adhesion', icon: BadgeCheck, label: 'Adhésion', show: true, key: 'adhesion' },
    { to: '/espace/carte', icon: CreditCard, label: 'Ma carte', show: true, key: 'carte' },
    { to: '/espace/recus', icon: Receipt, label: 'Mes reçus', show: true, key: 'recus' },
    { to: '/espace/mon-reseau', icon: Activity, label: 'Mon réseau', show: can('annuaire', 'view'), key: 'mon-reseau' },
    { to: '/espace/profil', icon: User, label: 'Profil', show: true, key: 'profil' },
  ].filter((i) => i.show);

  const showAskBell = can('messages', 'view') && perm === 'default';

  return (
    <div className="min-h-screen bg-[#F9F9F8] text-[#0F172A] font-sans antialiased pb-24 lg:pb-0" data-testid="member-layout">

      {/* ─── Top bar (asso branded, NO AssoPerf mention) ─── */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-30 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 shrink-0">
            {asso.logo_url ? (
              <img src={asso.logo_url} alt={asso.nom} className="w-10 h-10 rounded-xl object-cover shadow-sm shrink-0" />
            ) : (
              <div className="w-10 h-10 rounded-xl grid place-items-center text-white font-display font-black text-sm shadow-sm shrink-0" style={{ background: 'var(--theme-primary)' }}>
                {initials}
              </div>
            )}
            <div className="min-w-0 hidden xl:block">
              <div className="font-display font-bold text-[#0F172A] text-sm leading-tight truncate max-w-[180px]" data-testid="member-asso-name">{asso.nom}</div>
              <div className="text-[9px] uppercase tracking-[0.18em] text-[#94A3B8] font-bold mt-0.5">Espace membre</div>
            </div>
            <div className="min-w-0 hidden sm:block lg:hidden">
              <div className="font-display font-bold text-[#0F172A] text-sm leading-tight truncate max-w-[160px]" data-testid="member-asso-name-mobile">{asso.nom}</div>
            </div>
          </div>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-0.5 flex-1 justify-end overflow-x-auto min-w-0">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={`member-nav-${it.key}`}
                title={it.label}
                className={({ isActive }) =>
                  `relative flex items-center gap-1.5 px-2 py-2 rounded-xl text-sm whitespace-nowrap transition-colors ${
                    isActive
                      ? 'text-[var(--theme-primary)] bg-[var(--theme-primary-soft)] font-semibold'
                      : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] font-medium'
                  }`
                }
              >
                <it.icon className="w-4 h-4 shrink-0" /> <span className="hidden 2xl:inline">{it.label}</span>
                {it.badge > 0 && (
                  <span className="text-[9px] font-black text-white px-1.5 py-0.5 rounded-full leading-none" style={{ background: 'var(--theme-primary)' }}>
                    {it.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-1 shrink-0">
            {showAskBell && (
              <button onClick={askPermission} title="Activer les notifications" className="btn-ghost !p-2" data-testid="member-enable-notifs">
                <Bell className="w-4 h-4" />
              </button>
            )}
            {perm === 'denied' && (
              <span title="Notifications refusées dans le navigateur" className="text-[#CBD5E1] p-2">
                <BellOff className="w-4 h-4" />
              </span>
            )}
            <button onClick={logout} className="btn-ghost !p-2" data-testid="member-logout">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main>
        <Outlet context={{ asso }} />
      </main>

      {/* ─── Bottom nav mobile (scroll horizontal) ─── */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-white border-t border-[#E2E8F0] z-30 overflow-x-auto" data-testid="member-mobile-nav">
        <div className="flex min-w-max">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`member-mobile-${it.key}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 px-4 py-3 text-[9px] font-bold tracking-[0.14em] uppercase whitespace-nowrap relative transition-colors ${
                  isActive ? 'text-[var(--theme-primary)]' : 'text-[#94A3B8]'
                }`
              }
            >
              <span className="relative">
                <it.icon className="w-5 h-5" />
                {it.badge > 0 && (
                  <span className="absolute -top-1.5 -right-2 text-[9px] font-black text-white px-1 rounded-full leading-none" style={{ background: 'var(--theme-primary)' }}>
                    {it.badge}
                  </span>
                )}
              </span>
              {it.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
